// "Update Profile" takeover for the schedule-review side section. Rendered as a
// SideShell.Panel that layers over ScheduleWorkerProfilePanel; Back/Cancel return to the
// profile. Fetches the worker's term history for pre-fill and owns the save mutation; the
// form itself is the shared ScheduleWorkerProfileUpdateForm (WORK-6258).
import React from 'react';
import {
  Button,
  ButtonGroup,
  ErrorHandler,
  SideShell,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { todayIso } from '@common/services/datetime';
import { LOAD_BALANCING_WORKER_QUERY } from '@load-balancing/graphql/queries/load-balancing-worker';
import {
  ScheduleWorkerProfileUpdateForm,
  useScheduleWorkerProfileUpdateForm,
} from '@load-balancing/components/schedule-worker-profile-update-form';

export function ScheduleWorkerProfileUpdatePanel({
  workerId,
  onClose,
  schedulePeriodId,
  editingTermId = null,
  assignableRoles = [],
  chargeRole,
}: {
  workerId: string;
  onClose: () => void;
  schedulePeriodId?: string | null;
  // The scheduled change being edited (null = creating a new change).
  editingTermId?: string | null;
  // The service line's role abbreviations — the Role picker's options.
  assignableRoles?: readonly string[];
  // The service line's configured charge-role abbreviation (data-driven); hides the
  // charge-eligible checkbox when the selected role is the charge role.
  chargeRole?: string;
}) {
  const { data, loading, error } = useQuery(LOAD_BALANCING_WORKER_QUERY, {
    variables: { id: workerId, schedulingPeriodId: schedulePeriodId },
    skip: !workerId,
  });

  const worker = data?.loadBalancingWorker;

  // The form initialises from the worker's terms; the panel is opened from the profile panel,
  // which has already fetched this worker, so the cache is warm and terms are present on the
  // first render. While the query is still in flight the panel shows a loading skeleton.
  const form = useScheduleWorkerProfileUpdateForm({
    workerId,
    terms: worker?.terms ?? [],
    asOf: todayIso(),
    schedulingPeriodId: schedulePeriodId,
    editingTermId,
    onSaved: onClose,
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ScheduleWorkerProfileUpdatePanel"
        messageType="contextual"
      />
    );
  }

  return (
    <SideShell.Panel
      header="Update Profile"
      dataTestId="schedule-worker-profile-update-panel"
      loading={loading}
      footer={
        worker && (
          <ButtonGroup align="right">
            <Button kind="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button kind="primary" loading={form.saving} onClick={form.save}>
              Save
            </Button>
          </ButtonGroup>
        )
      }
    >
      {worker && (
        <ScheduleWorkerProfileUpdateForm
          values={form.values}
          onChange={form.update}
          saveAttempted={form.saveAttempted}
          serverErrors={form.serverErrors}
          datesDisabled={editingTermId !== null}
          assignableRoles={assignableRoles}
          chargeRole={chargeRole}
        />
      )}
    </SideShell.Panel>
  );
}
